import { useEffect, useMemo, useState } from 'react';
import './App.css';
import { firebaseReady, firebaseSetupMessage } from './firebase';
import { calculateQuote, isValidEmail, loadQuoteConfig, money, submitQuoteRequest } from './quoteService';

const emptyPage = { name: 'Home', lengthId: 'medium' };

function App() {
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === '1';
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [pages, setPages] = useState([emptyPage]);
  const [stylingOptionId, setStylingOptionId] = useState('');
  const [animationOptionId, setAnimationOptionId] = useState('');
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
  const [contact, setContact] = useState({ personName: '', companyName: '', email: '', phone: '', privacyAccepted: false });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadConfig() {
      if (!firebaseReady) {
        setLoading(false);
        return;
      }

      try {
        const loaded = await loadQuoteConfig();
        setConfig(loaded);
        setStylingOptionId(loaded.stylingOptions[0]?.id || '');
        setAnimationOptionId(loaded.animationOptions[0]?.id || '');
      } catch (err) {
        setError(err.message || 'Could not load quote settings.');
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, []);

  const estimate = useMemo(() => {
    if (!config) return null;
    return calculateQuote({ config, pages, stylingOptionId, animationOptionId, selectedFeatureIds });
  }, [animationOptionId, config, pages, selectedFeatureIds, stylingOptionId]);

  const enabledFeatures = useMemo(() => {
    if (!config) return [];
    return config.features
      .filter((feature) => feature.enabled !== false)
      .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
  }, [config]);

  function updatePage(index, field, value) {
    setPages((current) => current.map((page, pageIndex) => (
      pageIndex === index ? { ...page, [field]: value } : page
    )));
  }

  function addPage(name = 'Custom page') {
    setPages((current) => [...current, { name, lengthId: config.pageLengths[1]?.id || config.pageLengths[0]?.id || 'medium' }]);
  }

  function removePage(index) {
    setPages((current) => current.length > 1 ? current.filter((_, pageIndex) => pageIndex !== index) : current);
  }

  function toggleFeature(featureId) {
    setSelectedFeatureIds((current) => (
      current.includes(featureId) ? current.filter((id) => id !== featureId) : [...current, featureId]
    ));
  }

  function validateCurrentStep() {
    if (step === 0 && !pages.some((page) => page.name.trim())) return 'Add at least one page.';
    if (step === 1 && !stylingOptionId) return 'Choose a styling option.';
    if (step === 2 && !animationOptionId) return 'Choose an animation option.';
    return '';
  }

  function nextStep() {
    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setStep((current) => Math.min(current + 1, 5));
  }

  function previousStep() {
    setError('');
    setStep((current) => Math.max(current - 1, 0));
  }

  function contactDisplayName() {
    return contact.companyName.trim() || contact.personName.trim();
  }

  function validateContact() {
    if (!contactDisplayName()) return 'Enter either your name and surname or your company name.';
    if (!isValidEmail(contact.email)) return 'Enter a valid email address.';
    if (!contact.phone.trim()) return 'Enter a contact number.';
    if (!/^[+\d() -]{6,}$/.test(contact.phone.trim()) || contact.phone.replace(/\D/g, '').length < 6) return 'Enter a valid contact number.';
    if (!contact.privacyAccepted) return 'Please agree to the privacy policy before submitting.';
    return '';
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validateContact();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await submitQuoteRequest({
        config,
        pages: pages.filter((page) => page.name.trim()),
        stylingOptionId,
        animationOptionId,
        selectedFeatureIds,
        contact: { ...contact, displayName: contactDisplayName() },
      });
      setSubmitted(result);
    } catch (err) {
      setError(err.message || 'Could not submit the quote request.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!firebaseReady) {
    return <SetupMessage message={firebaseSetupMessage} />;
  }

  if (loading) {
    return <main className={`quote-app ${isEmbed ? 'is-embed' : ''}`}><div className="loading-panel">Loading estimator settings...</div></main>;
  }

  if (error && !config) {
    return <SetupMessage message={error} />;
  }

  if (!config.general.enabled) {
    return <SetupMessage message="The quote estimator is currently offline." />;
  }

  if (submitted) {
    return (
      <main className={`quote-app ${isEmbed ? 'is-embed' : ''}`}>
        <section className="success-panel">
          <span className="eyebrow"><span className="status-dot" /> Request received</span>
          <h1>Estimate submitted</h1>
          <p>Your request has been saved and a quote record has been generated.</p>
          <div className="result-grid">
            <strong>{money(submitted.estimate.totals.total, config.general.currency)}</strong>
            <span>{submitted.estimate.totalHours} estimated hours</span>
          </div>
        </section>
      </main>
    );
  }

  const steps = ['Pages', 'Styling', 'Animations', 'Features', 'Summary', 'Contact'];

  return (
    <main className={`quote-app ${isEmbed ? 'is-embed' : ''}`}>
      <section className="quote-shell">
        <aside className="quote-intro">
          <span className="eyebrow"><span className="status-dot" /> Estimate System Online</span>
          <h1>Website quote estimator</h1>
          <p>Build a structured website estimate from pages, styling needs, motion, and optional features.</p>
          <div className="estimate-card">
            <span className="meta-label">Live Estimate</span>
            <strong>{estimate ? money(estimate.totals.total, config.general.currency) : money(0, config.general.currency)}</strong>
            <small>{estimate?.totalHours || 0} hours at {money(config.general.hourlyRate, config.general.currency)}/hour</small>
          </div>
        </aside>

        <form className="quote-panel" onSubmit={handleSubmit}>
          <div className="wizard-progress" aria-label="Quote progress">
            {steps.map((item, index) => (
              <button
                className={index === step ? 'is-active' : ''}
                key={item}
                type="button"
                onClick={() => setStep(index)}
                aria-label={item}
                title={item}
              >
                {String(index + 1).padStart(2, '0')}
              </button>
            ))}
          </div>

          {error && <div className="message error">{error}</div>}

          {step === 0 && (
            <section className="wizard-step">
              <h2>Website pages</h2>
              <div className="suggestion-row">
                {(config.general.pageSuggestions || []).map((name) => (
                  <button className="chip-button" type="button" key={name} onClick={() => addPage(name)}>{name}</button>
                ))}
              </div>
              <div className="page-list">
                {pages.map((page, index) => (
                  <article className="page-row" key={`${page.name}-${index}`}>
                    <label>
                      Page name
                      <input value={page.name} onChange={(event) => updatePage(index, 'name', event.target.value)} readOnly={!config.general.allowCustomPageNames} />
                    </label>
                    <label>
                      Page length
                      <select value={page.lengthId} onChange={(event) => updatePage(index, 'lengthId', event.target.value)}>
                        {config.pageLengths.map((length) => (
                          <option key={length.id} value={length.id}>{length.label}</option>
                        ))}
                      </select>
                    </label>
                    <button className="icon-button" type="button" onClick={() => removePage(index)} aria-label="Remove page" title="Remove page">x</button>
                  </article>
                ))}
              </div>
              <button className="button secondary" type="button" onClick={() => addPage()}>Add page</button>
            </section>
          )}

          {step === 1 && (
            <ChoiceStep title="Styling / branding" options={config.stylingOptions} selectedId={stylingOptionId} onSelect={setStylingOptionId} />
          )}

          {step === 2 && (
            <ChoiceStep title="Animations" options={config.animationOptions} selectedId={animationOptionId} onSelect={setAnimationOptionId} />
          )}

          {step === 3 && (
            <section className="wizard-step">
              <h2>Additional features</h2>
              <div className="choice-grid">
                {enabledFeatures.map((feature) => (
                  <label className={`choice-card ${selectedFeatureIds.includes(feature.id) ? 'selected' : ''}`} key={feature.id}>
                    <input type="checkbox" checked={selectedFeatureIds.includes(feature.id)} onChange={() => toggleFeature(feature.id)} />
                    <span>
                      <strong>{feature.name}</strong>
                      <small>{feature.description}</small>
                      <em>{feature.hours}h {feature.category ? `// ${feature.category}` : ''}</em>
                    </span>
                  </label>
                ))}
                {!enabledFeatures.length && <p className="muted">No additional features are currently configured.</p>}
              </div>
            </section>
          )}

          {step === 4 && estimate && (
            <section className="wizard-step">
              <h2>Estimate summary</h2>
              <div className="summary-list">
                {estimate.lines.map((line) => (
                  <div className="summary-line" key={line.id}>
                    <span>{line.desc}</span>
                    <strong>{line.qty}h x {money(line.unit, config.general.currency)}</strong>
                  </div>
                ))}
              </div>
              <div className="summary-total">
                <span>{estimate.totalHours} estimated hours</span>
                <strong>{money(estimate.totals.total, config.general.currency)}</strong>
              </div>
            </section>
          )}

          {step === 5 && (
            <section className="wizard-step">
              <h2>Contact information</h2>
              <div className="form-grid">
                <label>Name and surname<input value={contact.personName} onChange={(event) => setContact({ ...contact, personName: event.target.value })} autoComplete="name" /></label>
                <label>Company name<input value={contact.companyName} onChange={(event) => setContact({ ...contact, companyName: event.target.value })} autoComplete="organization" /></label>
                <label>Email address<input type="email" value={contact.email} onChange={(event) => setContact({ ...contact, email: event.target.value })} autoComplete="email" required /></label>
                <label>Contact number<input value={contact.phone} onChange={(event) => setContact({ ...contact, phone: event.target.value })} autoComplete="tel" required /></label>
              </div>
              <PrivacyPolicy config={config} />
              <label className="privacy-check">
                <input type="checkbox" checked={contact.privacyAccepted} onChange={(event) => setContact({ ...contact, privacyAccepted: event.target.checked })} />
                <span>I have read and agree to the Privacy Policy and consent to my information being used to prepare this quote.</span>
              </label>
            </section>
          )}

          <div className="wizard-actions">
            <button className="button secondary" type="button" onClick={previousStep} disabled={step === 0 || submitting}>Back</button>
            {step < steps.length - 1 ? (
              <button className="button" type="button" onClick={nextStep}>Continue</button>
            ) : (
              <button className="button" type="submit" disabled={submitting || !contact.privacyAccepted}>{submitting ? 'Submitting' : 'Submit quote request'}</button>
            )}
          </div>
        </form>
      </section>
    </main>
  );
}

function ChoiceStep({ title, options, selectedId, onSelect }) {
  return (
    <section className="wizard-step">
      <h2>{title}</h2>
      <div className="choice-grid">
        {options.map((option) => (
          <label className={`choice-card ${selectedId === option.id ? 'selected' : ''}`} key={option.id}>
            <input type="radio" checked={selectedId === option.id} onChange={() => onSelect(option.id)} />
            <span>
              <strong>{option.label}</strong>
              <small>{option.description}</small>
              <em>{option.hours}h</em>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

function PrivacyPolicy({ config }) {
  const general = config.general;
  return (
    <details className="privacy-panel">
      <summary>Privacy Policy</summary>
      <div>
        <p>This estimator collects your name or company name, email address, contact number, website quote requirements, selected pages, styling option, animation option, selected additional features, calculated quote estimate, and submission date/time.</p>
        <p>The information is used to prepare and manage the requested quote, contact you about the quote, store you as a customer in the invoicing system, and generate an internal quote record. Data is stored in Firebase.</p>
        <p>Business: {general.privacyBusinessName || 'TODO: add business name in AI Quote Settings'}. Contact: {general.privacyContactEmail || 'TODO: add privacy contact email'}. Retention: {general.privacyRetentionPeriod || 'TODO: add retention period'}. Jurisdiction: {general.privacyJurisdiction || 'TODO: add jurisdiction'}.</p>
      </div>
    </details>
  );
}

function SetupMessage({ message }) {
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === '1';

  return (
    <main className={`quote-app ${isEmbed ? 'is-embed' : ''}`}>
      <section className="success-panel">
        <span className="eyebrow"><span className="status-dot" /> Setup required</span>
        <h1>Estimator unavailable</h1>
        <p>{message}</p>
      </section>
    </main>
  );
}

export default App;
